import {
  DOCTOR_NOTICE_BODY_MAX,
  finalizeTreatmentComment,
  TREATMENT_COMMENT_DUPLICATE_WARNING,
  TREATMENT_COMMENT_LABEL,
  treatmentCommentDuplicates,
  type DoctorNoticeConfirmations,
  type TreatmentCommentStructured,
} from "@care-by-ella/contracts";
import { doctorNoticeClaimErrors } from "@/lib/doctor-notice";

// TES-1290 (TES-1267 §2 B, §4) — "Läkarens kommentar till
// behandlingsbeslutet": the one patient free text of every treatment
// decision. The decision workspace, the one-step meeting approval, the
// transfer review, the monthly review and the running treatment's decision
// all use this field, so it reads, prefills and warns the same everywhere.
//
// `value` is null while the doctor has not typed in it: the field then shows
// `text`, which follows the prefill (doctorNoticeText), so changing the
// outcome changes the proposal. The caller holds the state and publishes
// finalizeTreatmentComment(text).
//
// Two kinds of message:
//   - a claim of an unconfirmed Webdoc action (`confirmed` given): the API
//     refuses it, so it is shown as an error;
//   - a restated structured value (medication, dose, interval, prescription
//     status, next dose): a warning only, since the patient sees those from
//     the order under Din behandling.
export function TreatmentCommentField({
  id,
  text,
  value,
  prefill,
  onChange,
  structured,
  confirmed,
  disabled,
  error,
  preview = true,
  labelHidden = false,
}: {
  id?: string;
  text: string;
  value: string | null;
  prefill: string;
  onChange: (next: string | null) => void;
  structured?: TreatmentCommentStructured;
  confirmed?: DoctorNoticeConfirmations;
  disabled?: boolean;
  error?: string;
  // The meeting previews it in its own publish step (TES-1317).
  preview?: boolean;
  // When a surrounding heading already names it (the monthly review's
  // section 4), the label stays for screen readers only.
  labelHidden?: boolean;
}) {
  const claims = confirmed ? doctorNoticeClaimErrors(text, confirmed) : [];
  const duplicates = treatmentCommentDuplicates(text, structured);
  const reset = value !== null && value !== prefill && prefill !== "";
  const published = finalizeTreatmentComment(text);
  const rows = [
    ["Läkemedel", structured?.medication],
    ["Dos", structured?.dose],
    ["Intervall", structured?.frequency],
  ].filter((r): r is [string, string] => !!r[1]?.trim());
  const textId = id ? `${id}-text` : undefined;
  return (
    <div id={id} className="flex flex-col gap-xs text-sm">
      <label
        htmlFor={textId}
        className={labelHidden ? "sr-only" : "font-semibold"}
      >
        {TREATMENT_COMMENT_LABEL}
      </label>
      <textarea
        id={textId}
        aria-invalid={claims.length > 0 || error ? true : undefined}
        value={text}
        onChange={(e) => onChange(e.target.value)}
        rows={Math.min(12, Math.max(4, text.split("\n").length + 1))}
        maxLength={DOCTOR_NOTICE_BODY_MAX}
        disabled={disabled}
        className="rounded-md border border-surfaceMuted p-sm text-sm focus:border-brand focus:outline-none"
      />
      <span className="text-xs text-textSecondary">
        Förifylld utifrån beslutet. Beskriv kort bedömningen, relevanta råd och
        uppföljningen. Läkemedel, dos, intervall, receptstatus och nästa dos
        visas för patienten från de strukturerade uppgifterna och skrivs inte
        här. Patienten kan inte svara på kommentaren.
      </span>
      {reset ? (
        <button
          type="button"
          onClick={() => onChange(null)}
          disabled={disabled}
          className="self-start text-xs font-semibold text-brand hover:underline disabled:opacity-50"
        >
          Återställ förslaget
        </button>
      ) : null}
      {duplicates.map((d) => (
        <p
          key={d}
          className="text-xs text-warning"
          data-testid="treatment-comment-duplicate"
        >
          {TREATMENT_COMMENT_DUPLICATE_WARNING[d]}
        </p>
      ))}
      {claims.map((message) => (
        <p key={message} className="text-xs text-danger">
          {message}
        </p>
      ))}
      {error ? <p className="text-xs text-danger">{error}</p> : null}
      {preview && published ? (
        <section
          className="mt-xs rounded-md border border-surfaceMuted bg-surfaceMuted/30 p-sm"
          data-testid="treatment-comment-preview"
        >
          <p className="text-xs font-semibold text-textSecondary">
            Förhandsvisning · Din behandling
          </p>
          {rows.length > 0 ? (
            <dl className="mt-xs grid grid-cols-[auto_1fr] gap-x-md gap-y-xs text-sm">
              {rows.map(([label, v]) => (
                <div key={label} className="contents">
                  <dt className="text-textSecondary">{label}</dt>
                  <dd>{v}</dd>
                </div>
              ))}
            </dl>
          ) : null}
          <p className="mt-sm text-xs text-textSecondary">
            {TREATMENT_COMMENT_LABEL}
          </p>
          <p className="mt-xs whitespace-pre-wrap rounded-sm border border-surfaceMuted bg-surface p-sm">
            {published}
          </p>
        </section>
      ) : null}
    </div>
  );
}
